/**
 * Section links: each chip scrolls to the page section whose id ends with `__<key>` (the section name in the template).
 * Chips whose section is missing are hidden, and so is the whole block when none is left.
 *
 * Markup hooks: `a[data-anchor-target]` (the chip; its parent `li` is hidden when the target is missing).
 */
class ProductAnchorLinks extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const chips = Array.from(this.querySelectorAll('[data-anchor-target]'));

    const findSection = (key) =>
      Array.from(document.querySelectorAll('.shopify-section')).find((section) => section.id.endsWith(`__${key}`));

    let visible = 0;
    chips.forEach((chip) => {
      const key = chip.getAttribute('data-anchor-target') || '';
      if (findSection(key)) {
        visible += 1;
      } else {
        const item = chip.closest('li');
        if (item) item.hidden = true;
      }
    });
    if (!visible) this.hidden = true;

    this.addEventListener(
      'click',
      (event) => {
        const chip = /** @type {Element} */ (event.target).closest('[data-anchor-target]');
        if (!chip || !this.contains(chip)) return;
        const section = findSection(chip.getAttribute('data-anchor-target') || '');
        if (!section) return;
        event.preventDefault();
        section.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        section.setAttribute('tabindex', '-1');
        section.focus({ preventScroll: true });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('product-anchor-links')) {
  customElements.define('product-anchor-links', ProductAnchorLinks);
}
